/**
 * releaseEta -- pure ETA formatting for the batch release progress indicator.
 *
 * RATE. Cumulative `done / elapsed` since `startedAt`, never per-poll, so the
 * number does not jitter between ticks. `done` (what actually shipped) drives
 * the rate; `resolved` (shipped + held back + abandoned) drives what is left,
 * because held-back items resolve but never ship and must not count as work
 * remaining.
 *
 * HONESTY. An unknown rate never prints a number:
 *   - no usable startedAt (missing, unparseable, or in the future from clock
 *     skew), or under 30s elapsed          -> "Estimating..."
 *   - done is 0 after the window            -> "Queued, waiting for an earlier
 *     batch" (batches of one store drain one after another; no cross-batch
 *     math is attempted)
 *   - drain not running, or empty batch     -> nothing
 */
export const ESTIMATE_WINDOW_MS = 30 * 1000;
const MIN_LIMIT_S = 90 * 60;

/**
 * The API serialises release_started_at from a TIMESTAMP WITHOUT TIME ZONE
 * column that is written in UTC, so the string has no zone designator, and
 * Date.parse would read it as the viewer's LOCAL time. Treat a zone-less
 * stamp as UTC; leave one with Z or an offset alone.
 */
function parseStartedAt(value) {
  if (!value) return NaN;
  const text = String(value);
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(text);
  return Date.parse(hasZone ? text : `${text}Z`);
}

export function releaseEta({ startedAt, itemCount, done, resolved, state, now }) {
  const none = { kind: "none", label: "" };
  if (state !== "releasing" || !itemCount) return none;

  const started = parseStartedAt(startedAt);
  const elapsedMs = now - started;
  if (!Number.isFinite(elapsedMs) || elapsedMs < ESTIMATE_WINDOW_MS) {
    return { kind: "estimating", label: "Estimating..." };
  }

  const remaining = itemCount - (resolved || 0);
  if (remaining <= 0) return { kind: "finishing", label: "Finishing up" };

  if (!done) {
    return { kind: "queued", label: "Queued, waiting for an earlier batch" };
  }

  const secondsLeft = (remaining * (elapsedMs / 1000)) / done;
  if (secondsLeft < 60) {
    return { kind: "eta", label: "less than a minute left" };
  }
  if (secondsLeft <= MIN_LIMIT_S) {
    return { kind: "eta", label: `about ${Math.round(secondsLeft / 60)} min left` };
  }
  return { kind: "eta", label: `about ${Math.round(secondsLeft / 3600)} hours left` };
}
